import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { apiErrorMessage } from '../api/client';
import * as testimonialsApi from '../api/testimonialsApi';
import { ConfirmDialog, EmptyState, ErrorBanner, Modal, Skeleton, Spinner } from '../components/ui';
import { IconEye, IconPencil, IconPlus, IconTrash } from '../components/icons';
import {
  CLUSTER_LABELS,
  NAME_MAX,
  QUOTE_MAX,
  QUOTE_MIN,
  Testimonial,
  isPlaceholder,
} from '../types/testimonial';

const EMPTY_FORM = {
  customer_name: '',
  customer_city: '',
  cluster_label: '',
  rating: 5,
  quote: '',
  is_active: true,
};

type TestimonialForm = typeof EMPTY_FORM;

function formFrom(t: Testimonial): TestimonialForm {
  return {
    customer_name: t.customer_name,
    customer_city: t.customer_city ?? '',
    cluster_label: t.cluster_label ?? '',
    rating: t.rating,
    quote: t.quote,
    is_active: t.is_active === 1,
  };
}

/** The card list truncates; the landing page never does. */
const PREVIEW_CHARS = 120;

function truncate(text: string, max = PREVIEW_CHARS): string {
  return text.length <= max ? text : `${text.slice(0, max).trimEnd()}…`;
}

/* ---------- Small pieces ---------- */

function Stars({ rating, className = '' }: { rating: number; className?: string }) {
  return (
    <span className={className} aria-label={`${rating} out of 5 stars`}>
      {[1, 2, 3, 4, 5].map((n) => (
        <span key={n} className={n <= rating ? 'text-gold' : 'text-gold/25'} aria-hidden="true">
          {n <= rating ? '★' : '☆'}
        </span>
      ))}
    </span>
  );
}

/** Click a star to set the rating. Five buttons, not a slider — it reads as what it sets. */
function StarPicker({ value, onChange }: { value: number; onChange: (n: number) => void }) {
  return (
    <div className="flex items-center gap-1">
      {[1, 2, 3, 4, 5].map((n) => (
        <button
          key={n}
          type="button"
          onClick={() => onChange(n)}
          aria-label={`${n} star${n === 1 ? '' : 's'}`}
          aria-pressed={value === n}
          className={`text-xl leading-none transition-colors ${
            n <= value ? 'text-gold' : 'text-sub/40 hover:text-gold/60'
          }`}
        >
          {n <= value ? '★' : '☆'}
        </button>
      ))}
      <span className="ml-2 text-xs text-sub">{value} of 5</span>
    </div>
  );
}

/** The same on/off switch the coupons page uses. */
function Toggle({
  label,
  description,
  checked,
  onChange,
}: {
  label: string;
  description?: string;
  checked: boolean;
  onChange: () => void;
}) {
  return (
    <div className="flex items-start justify-between gap-4">
      <div className="min-w-0">
        <p className="text-sm font-medium">{label}</p>
        {description && <p className="mt-0.5 text-xs text-sub">{description}</p>}
      </div>
      <button
        type="button"
        role="switch"
        aria-label={label}
        aria-checked={checked}
        onClick={onChange}
        className={`relative mt-0.5 h-6 w-11 shrink-0 cursor-pointer rounded-full transition-colors ${
          checked ? 'bg-gold' : 'bg-edge'
        }`}
      >
        <span
          className={`absolute top-0.5 h-5 w-5 rounded-full bg-white shadow transition-all ${
            checked ? 'left-[22px]' : 'left-0.5'
          }`}
        />
      </button>
    </div>
  );
}

/**
 * The quote as the landing page will render it.
 *
 * Deliberately styled against the STOREFRONT's palette (near-black ground,
 * #e8dcc0 gold, italic serif-ish quote) rather than the admin's own theme: the
 * point of a preview is to answer "how will this look to a customer", and a
 * preview that adopts the admin's light mode answers a different question. The
 * hard-coded colours are the landing band's, mirrored from
 * frontend/src/components/landing/Testimonials.tsx.
 */
function LandingPreview({ form }: { form: TestimonialForm }) {
  const quote = form.quote.trim();
  const name = form.customer_name.trim();

  return (
    <div className="rounded-xl border border-edge bg-[#0f0c0a] px-6 py-8 text-center">
      <Stars rating={form.rating} className="text-sm tracking-[0.22em]" />

      <p
        className="mx-auto mt-5 max-w-md text-[15px] font-light italic leading-[1.7] text-[#e8e8f0]"
        style={{ fontFamily: 'Georgia, serif' }}
      >
        {quote ? `“${quote}”` : <span className="not-italic text-[#e8e8f0]/35">The quote will appear here…</span>}
      </p>

      <p className="mt-6 text-xs tracking-[0.1em] text-[#e8e8f0]/50">
        — {name || 'Customer name'}
        {form.customer_city.trim() ? `, ${form.customer_city.trim()}` : ''}
      </p>

      {/* Same condition as the landing card: no cluster, no pill. */}
      {form.cluster_label && (
        <span className="mt-3 inline-block rounded-full border border-[#e8dcc0]/20 bg-[#e8dcc0]/[0.06] px-3 py-1 text-[10px] uppercase tracking-[0.2em] text-[#e8dcc0]/70">
          {form.cluster_label}
        </span>
      )}

      {!form.is_active && (
        <p className="mt-5 text-[11px] uppercase tracking-wider text-sub">
          Hidden — this will not appear on the landing page
        </p>
      )}
    </div>
  );
}

/* ---------- Page ---------- */

export default function TestimonialsPage() {
  const [items, setItems] = useState<Testimonial[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [actionError, setActionError] = useState('');
  const [actionSuccess, setActionSuccess] = useState('');

  // One modal serves both jobs. `editTarget` is the row being edited — null
  // while creating — and it is what addresses the record on save.
  const [showForm, setShowForm] = useState(false);
  const [editTarget, setEditTarget] = useState<Testimonial | null>(null);
  const [form, setForm] = useState<TestimonialForm>(EMPTY_FORM);
  const [saving, setSaving] = useState(false);

  const [togglingId, setTogglingId] = useState('');
  const [deleteTarget, setDeleteTarget] = useState<Testimonial | null>(null);
  const [deleting, setDeleting] = useState(false);

  // Which card is under the cursor mid-drag. `dragId` also gates the native
  // draggable attribute below, so a card can only be picked up by its handle.
  const [dragId, setDragId] = useState('');
  const [overId, setOverId] = useState('');
  const [reordering, setReordering] = useState(false);

  const load = useCallback(async () => {
    setLoading(true);
    setError('');
    try {
      setItems(await testimonialsApi.getTestimonials());
    } catch (err) {
      setError(apiErrorMessage(err));
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    load();
  }, [load]);

  // Disarm the drag handle on ANY mouse release, not just a successful drop.
  // Pressing the handle and letting go without moving fires no dragend, which
  // would leave the card armed and greyed out until the page reloaded.
  useEffect(() => {
    if (!dragId) return;
    const clear = () => setDragId('');
    window.addEventListener('mouseup', clear);
    return () => window.removeEventListener('mouseup', clear);
  }, [dragId]);

  const placeholdersLeft = useMemo(() => items.filter(isPlaceholder).length, [items]);

  const openCreate = () => {
    setEditTarget(null);
    setForm(EMPTY_FORM);
    setActionError('');
    setActionSuccess('');
    setShowForm(true);
  };

  const openEdit = (t: Testimonial) => {
    setEditTarget(t);
    setForm(formFrom(t));
    setActionError('');
    setActionSuccess('');
    setShowForm(true);
  };

  const quoteLen = form.quote.trim().length;
  const canSave =
    form.customer_name.trim().length > 0 &&
    form.customer_name.trim().length <= NAME_MAX &&
    quoteLen >= QUOTE_MIN &&
    quoteLen <= QUOTE_MAX;

  const handleSave = async () => {
    // Blank optionals go as explicit nulls so an edit can genuinely CLEAR a city
    // or a cluster rather than being read as "no change".
    const payload = {
      customer_name: form.customer_name.trim(),
      customer_city: form.customer_city.trim() || null,
      cluster_label: form.cluster_label || null,
      quote: form.quote.trim(),
      rating: form.rating,
      is_active: form.is_active,
    };

    setSaving(true);
    setActionError('');
    setActionSuccess('');
    try {
      const wasEdit = editTarget !== null;
      if (editTarget) {
        await testimonialsApi.updateTestimonial(editTarget.id, payload);
      } else {
        await testimonialsApi.createTestimonial(payload);
      }
      setShowForm(false);
      setEditTarget(null);
      setForm(EMPTY_FORM);
      // Reload before announcing, so the card the admin looks at next is the
      // saved one and not the stale copy the message claims was updated.
      await load();
      setActionSuccess(wasEdit ? 'Testimonial updated' : 'Testimonial added');
    } catch (err) {
      setActionError(apiErrorMessage(err));
    } finally {
      setSaving(false);
    }
  };

  const handleToggle = async (t: Testimonial) => {
    setTogglingId(t.id);
    setActionError('');
    setActionSuccess('');
    try {
      await testimonialsApi.toggleTestimonial(t.id);
      await load();
    } catch (err) {
      setActionError(apiErrorMessage(err));
    } finally {
      setTogglingId('');
    }
  };

  const handleDelete = async () => {
    if (!deleteTarget) return;
    setDeleting(true);
    setActionError('');
    setActionSuccess('');
    try {
      await testimonialsApi.deleteTestimonial(deleteTarget.id);
      setDeleteTarget(null);
      await load();
      setActionSuccess('Testimonial deleted');
    } catch (err) {
      setActionError(apiErrorMessage(err));
    } finally {
      setDeleting(false);
    }
  };

  /**
   * Commit a drag: move `dragId` to where `targetId` sits, then renumber.
   *
   * display_order is rewritten as 1..n across the WHOLE list rather than only
   * for the card that moved. Moving one row shifts the position of every row
   * between its old and new slot, and the endpoint takes one id at a time, so
   * anything less leaves duplicate or gapped numbers behind — and duplicates are
   * exactly what makes the landing carousel's order look random. Only rows whose
   * number actually changes are sent.
   */
  const commitReorder = async (targetId: string) => {
    const from = items.findIndex((t) => t.id === dragId);
    const to = items.findIndex((t) => t.id === targetId);
    setDragId('');
    setOverId('');
    if (from < 0 || to < 0 || from === to) return;

    const next = [...items];
    const [moved] = next.splice(from, 1);
    next.splice(to, 0, moved);

    // Optimistic: the list settles under the cursor immediately, then the writes
    // go out. A failure reloads from the server, which is the truth either way.
    const renumbered = next.map((t, i) => ({ ...t, display_order: i + 1 }));
    setItems(renumbered);

    const changed = renumbered.filter(
      (t) => t.display_order !== items.find((o) => o.id === t.id)?.display_order,
    );
    if (!changed.length) return;

    setReordering(true);
    setActionError('');
    try {
      for (const t of changed) {
        await testimonialsApi.reorderTestimonial(t.id, t.display_order);
      }
      await load();
    } catch (err) {
      setActionError(apiErrorMessage(err));
      await load();
    } finally {
      setReordering(false);
    }
  };

  return (
    <div className="space-y-6">
      <div className="flex flex-wrap items-center justify-between gap-3">
        <div>
          <h1 className="text-xl font-semibold">Testimonials</h1>
          <p className="text-sm text-sub">Manage customer quotes shown on the landing page</p>
        </div>
        <button className="btn-gold" onClick={openCreate}>
          <IconPlus className="h-4 w-4" />
          Add testimonial
        </button>
      </div>

      {error && <ErrorBanner message={error} onRetry={load} />}
      {actionError && <ErrorBanner message={actionError} />}
      {actionSuccess && (
        <div className="rounded-xl border border-green-500/40 bg-green-500/10 px-4 py-3 text-sm text-green-600 dark:text-green-300">
          {actionSuccess}
        </div>
      )}

      {/* The seeded quotes are fiction. Say so, loudly, until the last one is
          gone — a placeholder that survives to launch is a fake review on a
          live storefront. */}
      {placeholdersLeft > 0 && (
        <div className="rounded-xl border border-warn/40 bg-warn/10 px-4 py-3 text-sm text-warn">
          {placeholdersLeft} placeholder {placeholdersLeft === 1 ? 'quote is' : 'quotes are'} still
          live. These were seeded so the landing page is never empty — replace them with real
          customer quotes before launch.
        </div>
      )}

      {loading ? (
        <div className="space-y-3">
          {Array.from({ length: 3 }).map((_, i) => (
            <Skeleton key={i} className="h-40 w-full" />
          ))}
        </div>
      ) : items.length === 0 ? (
        <div className="card">
          <EmptyState
            message="No testimonials yet."
            hint="Add your first customer quote to show social proof on the landing page."
          />
          <div className="flex justify-center pb-8">
            <button className="btn-gold" onClick={openCreate}>
              <IconPlus className="h-4 w-4" />
              Add testimonial
            </button>
          </div>
        </div>
      ) : (
        <div className={`space-y-3 ${reordering ? 'pointer-events-none opacity-60' : ''}`}>
          {items.map((t) => (
            <div
              key={t.id}
              // draggable is granted only while the handle is held — otherwise
              // selecting the quote text would start a drag.
              draggable={dragId === t.id}
              onDragStart={(e) => {
                // Firefox refuses to start a drag without transfer data.
                e.dataTransfer.setData('text/plain', t.id);
                e.dataTransfer.effectAllowed = 'move';
              }}
              onDragOver={(e) => {
                if (!dragId || dragId === t.id) return;
                e.preventDefault();
                setOverId(t.id);
              }}
              onDrop={(e) => {
                e.preventDefault();
                commitReorder(t.id);
              }}
              onDragEnd={() => {
                setDragId('');
                setOverId('');
              }}
              className={`card p-5 transition-colors ${
                overId === t.id ? 'border-gold/60' : ''
              } ${dragId === t.id ? 'opacity-50' : ''}`}
            >
              <div className="flex flex-wrap items-start gap-3">
                <button
                  type="button"
                  // Armed on mouse-down, disarmed by the window mouseup above.
                  // Mouse only: native HTML5 drag-and-drop never fires for touch,
                  // so arming on touchstart would leave a card stuck mid-drag
                  // with no gesture able to finish it. Reordering on a phone is
                  // a rare enough job to leave to a desktop.
                  onMouseDown={() => setDragId(t.id)}
                  className="mt-0.5 cursor-grab select-none px-1 text-lg leading-none text-sub active:cursor-grabbing"
                  aria-label={`Reorder ${t.customer_name}`}
                  title="Drag to reorder"
                >
                  ⠿
                </button>

                <span className="mt-0.5 text-sm font-semibold tabular-nums text-sub">
                  #{t.display_order}
                </span>

                <div className="min-w-0 flex-1">
                  <p className="truncate text-sm font-semibold">
                    {t.customer_name}
                    {t.customer_city ? `, ${t.customer_city}` : ''}
                  </p>
                  <div className="mt-1 flex flex-wrap items-center gap-3">
                    <Stars rating={t.rating} className="text-sm" />
                    {t.cluster_label && (
                      <span className="text-xs uppercase tracking-wider text-sub">
                        {t.cluster_label}
                      </span>
                    )}
                    {isPlaceholder(t) && (
                      <span className="rounded-full border border-warn/40 bg-warn/10 px-2 py-0.5 text-[10px] font-semibold uppercase tracking-wider text-warn">
                        Placeholder
                      </span>
                    )}
                  </div>
                </div>

                <span
                  className={`inline-flex items-center gap-1.5 rounded-full px-2.5 py-1 text-xs font-medium ${
                    t.is_active === 1 ? 'bg-ok/10 text-ok' : 'bg-edge/60 text-sub'
                  }`}
                >
                  {t.is_active === 1 ? '● Active' : '○ Hidden'}
                </span>
              </div>

              <p className="mt-3 text-sm italic text-sub">“{truncate(t.quote)}”</p>

              <div className="mt-4 flex flex-wrap gap-2">
                <button className="btn-outline !py-1.5 !text-xs" onClick={() => openEdit(t)}>
                  <IconPencil className="h-3.5 w-3.5" />
                  Edit
                </button>
                <button
                  className="btn-outline !py-1.5 !text-xs"
                  onClick={() => handleToggle(t)}
                  disabled={togglingId === t.id}
                >
                  {togglingId === t.id ? <Spinner className="h-3.5 w-3.5" /> : <IconEye className="h-3.5 w-3.5" />}
                  {t.is_active === 1 ? 'Hide' : 'Show'}
                </button>
                <button className="btn-ghost !py-1.5 !text-xs hover:text-bad" onClick={() => setDeleteTarget(t)}>
                  <IconTrash className="h-3.5 w-3.5" />
                  Delete
                </button>
              </div>
            </div>
          ))}
        </div>
      )}

      {/* ---------- Add / edit ---------- */}
      <Modal
        open={showForm}
        title={editTarget ? 'Edit testimonial' : 'Add testimonial'}
        onClose={() => setShowForm(false)}
        size="lg"
        footer={
          <>
            <button className="btn-outline" onClick={() => setShowForm(false)} disabled={saving}>
              Cancel
            </button>
            <button className="btn-gold" onClick={handleSave} disabled={!canSave || saving}>
              {saving && <Spinner />}
              Save
            </button>
          </>
        }
      >
        <div className="space-y-5">
          <div className="grid gap-4 sm:grid-cols-2">
            <div>
              <label className="label" htmlFor="t-name">
                Customer name *
              </label>
              <input
                id="t-name"
                className="input"
                maxLength={NAME_MAX}
                value={form.customer_name}
                onChange={(e) => setForm({ ...form, customer_name: e.target.value })}
                placeholder="Rahul M."
              />
              <p className="mt-1 text-xs text-sub">Use first name + last initial, e.g. Rahul M.</p>
            </div>

            <div>
              <label className="label" htmlFor="t-city">
                City (optional)
              </label>
              <input
                id="t-city"
                className="input"
                value={form.customer_city}
                onChange={(e) => setForm({ ...form, customer_city: e.target.value })}
                placeholder="Mumbai"
              />
            </div>

            <div>
              <label className="label" htmlFor="t-cluster">
                Fragrance cluster (optional)
              </label>
              <select
                id="t-cluster"
                className="input"
                value={form.cluster_label}
                onChange={(e) => setForm({ ...form, cluster_label: e.target.value })}
              >
                <option value="">— None —</option>
                {CLUSTER_LABELS.map((c) => (
                  <option key={c} value={c}>
                    {c}
                  </option>
                ))}
              </select>
              <p className="mt-1 text-xs text-sub">
                Shown as a pill under the name. Leave as None to hide it.
              </p>
            </div>

            <div>
              <span className="label">Rating</span>
              <StarPicker value={form.rating} onChange={(rating) => setForm({ ...form, rating })} />
            </div>
          </div>

          <div>
            <label className="label" htmlFor="t-quote">
              Testimonial quote *
            </label>
            <textarea
              id="t-quote"
              className="input min-h-[120px] resize-y"
              maxLength={QUOTE_MAX}
              value={form.quote}
              onChange={(e) => setForm({ ...form, quote: e.target.value })}
              placeholder="Write in the customer's voice…"
            />
            <div className="mt-1 flex flex-wrap items-baseline justify-between gap-2">
              <p className="text-xs text-sub">
                Write in the customer's voice. The best quotes mention their personality or how the
                formula felt personal.
              </p>
              {/* The landing page never truncates a quote, so the count is the
                  only thing standing between a long one and a wall of italics. */}
              <span
                className={`text-xs tabular-nums ${
                  quoteLen > 0 && quoteLen < QUOTE_MIN ? 'text-warn' : 'text-sub'
                }`}
              >
                {quoteLen}/{QUOTE_MAX}
                {quoteLen > 0 && quoteLen < QUOTE_MIN ? ` — min ${QUOTE_MIN}` : ''}
              </span>
            </div>
          </div>

          <Toggle
            label="Active"
            description="Show on landing page"
            checked={form.is_active}
            onChange={() => setForm({ ...form, is_active: !form.is_active })}
          />

          <div>
            <p className="label">Landing page preview</p>
            <LandingPreview form={form} />
          </div>
        </div>
      </Modal>

      <ConfirmDialog
        open={deleteTarget !== null}
        title="Delete testimonial"
        danger
        busy={deleting}
        confirmLabel="Delete"
        message={
          <>
            Delete the quote from <strong>{deleteTarget?.customer_name}</strong>? This cannot be
            undone — to take it off the landing page without losing it, use Hide instead.
          </>
        }
        onConfirm={handleDelete}
        onClose={() => setDeleteTarget(null)}
      />
    </div>
  );
}
